import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { AlertCircle, Loader2, CheckCircle, ArrowLeft, Eye, EyeOff, Mail, Lock, User, Phone, Sparkles, Heart } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import AuthLayout from '../components/AuthLayout';

const RegisterPage = () => {
  const { register, isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={user?.role === 'admin' ? '/admin' : '/mon-espace'} replace />;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    const userData = {
      email: formData.email,
      password: formData.password,
      first_name: formData.first_name,
      last_name: formData.last_name,
      phone: formData.phone || null
    };

    try {
      const result = await register(userData);
      if (result.success) setSuccess(true);
      else setError(result.error);
    } catch {
      setError('Impossible de créer votre compte pour le moment. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  if (success) return <AuthLayout title="Bienvenue chez Ali." description="Votre compte a été créé. Connectez-vous pour choisir votre soin."><Link to="/connexion" className="korean-button">Me connecter</Link></AuthLayout>;
  return <AuthLayout title={<>Votre prochain<br /><em>moment pour vous.</em></>} description="Créez votre compte pour réserver et profiter de votre carte de fidélité.">
    <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <Alert variant="destructive" className="border-red-200 bg-red-50/50 backdrop-blur-sm">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-red-800 text-sm font-medium">{error}</AlertDescription>
                  </Alert>
                )}
                
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-500" />
                      Prénom
                    </Label>
                    <div className="relative">
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="Prénom"
                        required
                        disabled={loading}
                        className="h-11 pl-11 pr-4 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 rounded-xl text-sm transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-500" />
                      Nom
                    </Label>
                    <div className="relative">
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Nom"
                        required
                        disabled={loading}
                        className="h-11 pl-11 pr-4 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 rounded-xl text-sm transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                
                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                    <Mail className="w-4 h-4 text-purple-500" />
                    Adresse email
                  </Label>
                  <div className="relative">
                    <Input
                      id="email"
                      name="email"
                      type="email" autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      required
                      disabled={loading}
                      className="h-11 pl-11 pr-4 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 rounded-xl text-sm transition-all duration-200 bg-white/80 backdrop-blur-sm"
                    />
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                
                {/* Phone Field */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                    <Phone className="w-4 h-4 text-purple-500" />
                    Téléphone <span className="text-gray-400 text-xs">(optionnel)</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+33 1 23 45 67 89"
                      disabled={loading}
                      className="h-11 pl-11 pr-4 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 rounded-xl text-sm transition-all duration-200 bg-white/80 backdrop-blur-sm"
                    />
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                
                {/* Password Fields */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4 text-purple-500" />
                      Mot de passe
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password" autoComplete="new-password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        required
                        disabled={loading}
                        className="h-11 pl-11 pr-11 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 rounded-xl text-sm transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      />
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <button
                        type="button"
                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"} onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-gray-700 font-semibold text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4 text-purple-500" />
                      Confirmer le mot de passe
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword" autoComplete="new-password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="••••••••"
                        required
                        disabled={loading}
                        className="h-11 pl-11 pr-11 border-2 border-gray-200 focus:border-purple-400 focus:ring-purple-400/20 rounded-xl text-sm transition-all duration-200 bg-white/80 backdrop-blur-sm"
                      />
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <button
                        type="button"
                        aria-label={showConfirmPassword ? "Masquer la confirmation" : "Afficher la confirmation"} onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl text-base" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-3 h-5 w-5 animate-spin" />
                      Création du compte...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Créer mon compte
                    </>
                  )}
                </Button>
              </form>
    <div className="korean-auth-links"><p>Déjà un compte ? <Link to="/connexion">Se connecter</Link></p></div>
  </AuthLayout>;
};
export default RegisterPage;
